/**
 * Staff — API Keys & ERP Integrations (iter-50h)
 *
 * Admin-only page to create long-lived API keys for ERP / partner systems.
 * Each key is shown ONCE on creation. Keys carry a set of scopes and can
 * be revoked at any time. Revoked keys can then be permanently deleted.
 */
import { useEffect, useState } from "react";
import { KeyRound, Plus, Trash2, ShieldOff, Copy, ExternalLink, AlertCircle, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const SCOPE_DESCRIPTIONS = {
  "offers:read": "Lire le catalogue des offres (Day Pass, Sunset, B Brunch, Hébergement…)",
  "availability:read": "Vérifier la disponibilité d'une offre à une date donnée",
  "bookings:read": "Lire les réservations (liste paginée + détail)",
  "bookings:write": "Créer des réservations depuis l'ERP",
  "registrations:read": "Lire les auto-enregistrements (personnel, prestataires, invités)",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function StaffApiKeys() {
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", scopes: [] });
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null); // {key, name, scopes} — shown once
  const [copied, setCopied] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [k, s] = await Promise.all([
        api.get("/staff/api-keys"),
        api.get("/staff/api-keys/scopes"),
      ]);
      setKeys(k.data || []);
      setScopes(s.data?.scopes || []);
    } catch {
      toast.error("Impossible de charger les clés API");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function toggleScope(s) {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(s) ? f.scopes.filter(x => x !== s) : [...f.scopes, s],
    }));
  }

  async function submitCreate() {
    if (!form.name.trim()) return toast.error("Le nom est obligatoire");
    if (!form.scopes.length) return toast.error("Sélectionnez au moins un scope");
    setCreating(true);
    try {
      const { data } = await api.post("/staff/api-keys", form);
      setNewKey(data);
      setShowCreate(false);
      setForm({ name: "", scopes: [] });
      toast.success("Clé créée — copiez-la maintenant, elle ne sera plus affichée.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible");
    } finally {
      setCreating(false);
    }
  }

  async function revoke(id) {
    if (!window.confirm("Révoquer cette clé ? L'ERP perdra l'accès immédiatement.")) return;
    try {
      await api.post(`/staff/api-keys/${id}/revoke`);
      toast.success("Clé révoquée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Révocation impossible");
    }
  }

  async function destroy(id) {
    if (!window.confirm("Supprimer définitivement cette clé révoquée ?")) return;
    try {
      await api.delete(`/staff/api-keys/${id}`);
      toast.success("Clé supprimée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  }

  function copy(text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const base = (typeof window !== "undefined" ? window.location.origin : "")
    .replace("reserve-bbr.preview.emergentagent.com", "workflow-boulaybeachresort.com");

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-6xl mx-auto space-y-6" data-testid="staff-api-keys">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#B8922A] mb-1">Intégrations</div>
          <h1 className="font-display-serif text-2xl sm:text-3xl text-[#0A0A0A] leading-tight">
            Clés API · ERP & Partenaires
          </h1>
          <p className="text-sm text-[#0A0A0A]/55 mt-1">
            Émettez des clés long-vie à destination d'intégrateurs externes (ERP, channel-manager, BI).
            Chaque clé est limitée par scope et révocable instantanément.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-gold inline-flex items-center gap-2 px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em]"
          data-testid="api-keys-create-btn"
        >
          <Plus size={16} /> Nouvelle clé
        </button>
      </div>

      {/* Doc box */}
      <div className="border border-[#B8922A]/30 bg-[#B8922A]/5 p-5">
        <div className="flex items-start gap-3">
          <KeyRound size={18} className="text-[#B8922A] flex-shrink-0 mt-0.5" />
          <div className="space-y-2 text-sm text-[#0A0A0A]/80">
            <p>
              Les intégrateurs envoient le header{" "}
              <code className="bg-[#0A0A0A]/5 px-1.5 py-0.5 text-xs">X-API-Key: bbr_xxxx</code>
              {" "}sur les endpoints{" "}
              <code className="bg-[#0A0A0A]/5 px-1.5 py-0.5 text-xs">/api/integration/*</code>.
            </p>
            <div className="flex flex-wrap gap-3 text-xs">
              <a href={`${base}/api/docs`} target="_blank" rel="noreferrer"
                className="text-[#B8922A] hover:underline inline-flex items-center gap-1">
                <ExternalLink size={12} /> Swagger UI
              </a>
              <a href={`${base}/api/redoc`} target="_blank" rel="noreferrer"
                className="text-[#B8922A] hover:underline inline-flex items-center gap-1">
                <ExternalLink size={12} /> ReDoc
              </a>
              <a href={`${base}/api/openapi.json`} target="_blank" rel="noreferrer"
                className="text-[#B8922A] hover:underline inline-flex items-center gap-1">
                <ExternalLink size={12} /> openapi.json
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* New key callout — shown once after creation */}
      {newKey && (
        <div className="border-2 border-[#B8922A] bg-white p-5 sm:p-6" data-testid="api-key-just-created">
          <div className="flex items-start gap-3 mb-3">
            <AlertCircle size={20} className="text-[#B8922A] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="font-display-serif text-xl text-[#0A0A0A] mb-1">
                Clé "{newKey.name}" générée
              </h2>
              <p className="text-sm text-[#0A0A0A]/65">
                ⚠️ Copiez-la dès maintenant — elle ne sera plus affichée ensuite.
              </p>
            </div>
            <button
              onClick={() => setNewKey(null)}
              className="text-[#0A0A0A]/40 hover:text-[#0A0A0A] text-2xl leading-none"
              aria-label="Fermer"
            >×</button>
          </div>
          <div className="bg-[#0A0A0A] text-[#F5F0E8] p-3 sm:p-4 font-mono text-xs sm:text-sm break-all relative">
            {newKey.key}
            <button
              onClick={() => copy(newKey.key)}
              className="absolute top-2 right-2 bg-[#B8922A] hover:bg-[#a37e1f] text-white px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 transition-colors"
              data-testid="copy-new-key-btn"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copié" : "Copier"}
            </button>
          </div>
        </div>
      )}

      {/* Keys list */}
      <div className="bg-white border border-[#0A0A0A]/8">
        <div className="px-5 py-3 border-b border-[#0A0A0A]/8 text-[0.65rem] uppercase tracking-[0.3em] text-[#0A0A0A]/55">
          Clés émises {!loading && `(${keys.length})`}
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#0A0A0A]/50">Chargement…</div>
        ) : keys.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#0A0A0A]/55">
            Aucune clé pour le moment.
          </div>
        ) : (
          <ul className="divide-y divide-[#0A0A0A]/8">
            {keys.map((k) => (
              <li key={k.id} className="p-5 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3" data-testid={`api-key-${k.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display-serif text-base text-[#0A0A0A]">{k.name}</div>
                    {k.revoked ? (
                      <span className="px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.18em] bg-red-50 text-red-700 border border-red-200">
                        Révoquée
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.18em] bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Active
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 text-xs text-[#0A0A0A]/55 font-mono">
                    {k.prefix}••••••••••••••••••••••••
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(k.scopes || []).map((s) => (
                      <span key={s} className="text-[0.6rem] uppercase tracking-[0.16em] px-2 py-0.5 bg-[#B8922A]/10 text-[#B8922A] border border-[#B8922A]/20">
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-[#0A0A0A]/50 space-x-3">
                    <span>Créée le {fmtDate(k.created_at)} par {k.created_by}</span>
                    <span>· {k.usage_count} appel{k.usage_count > 1 ? "s" : ""}</span>
                    {k.last_used_at && <span>· dernier appel {fmtDate(k.last_used_at)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!k.revoked ? (
                    <button
                      onClick={() => revoke(k.id)}
                      className="text-[0.65rem] uppercase tracking-[0.18em] px-3 py-2 border border-[#0A0A0A]/20 text-[#0A0A0A]/70 hover:border-red-400 hover:text-red-600 inline-flex items-center gap-1.5"
                      data-testid={`revoke-${k.id}`}
                    >
                      <ShieldOff size={12} /> Révoquer
                    </button>
                  ) : (
                    <button
                      onClick={() => destroy(k.id)}
                      className="text-[0.65rem] uppercase tracking-[0.18em] px-3 py-2 border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5"
                      data-testid={`delete-${k.id}`}
                    >
                      <Trash2 size={12} /> Supprimer
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div
          className="fixed inset-0 bg-black/55 z-50 flex items-center justify-center p-4"
          onClick={() => !creating && setShowCreate(false)}
          data-testid="create-key-modal"
        >
          <div
            className="bg-white text-[#0A0A0A] w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 sm:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#B8922A] mb-2">Nouvelle clé API</div>
            <h2 className="font-display-serif text-2xl mb-6">Émettre une clé</h2>
            <div className="space-y-5">
              <div>
                <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 mb-2">
                  Nom de l&apos;intégrateur
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex. ERP — Wave Hôtellerie"
                  className="w-full border border-[#0A0A0A]/15 px-4 py-2.5 text-sm focus:border-[#B8922A] outline-none"
                  data-testid="new-key-name"
                />
              </div>
              <div>
                <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 mb-3">
                  Permissions (scopes)
                </label>
                <div className="space-y-2">
                  {scopes.map((s) => (
                    <label
                      key={s}
                      className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                        form.scopes.includes(s)
                          ? "border-[#B8922A] bg-[#B8922A]/5"
                          : "border-[#0A0A0A]/10 hover:border-[#B8922A]/40"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={form.scopes.includes(s)}
                        onChange={() => toggleScope(s)}
                        className="mt-1 accent-[#B8922A]"
                        data-testid={`scope-${s}`}
                      />
                      <div className="flex-1">
                        <div className="font-mono text-xs text-[#0A0A0A]">{s}</div>
                        <div className="text-xs text-[#0A0A0A]/60 mt-0.5">
                          {SCOPE_DESCRIPTIONS[s] || ""}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-7 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowCreate(false)}
                disabled={creating}
                className="px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] text-[#0A0A0A]/65 hover:text-[#0A0A0A]"
              >
                Annuler
              </button>
              <button
                onClick={submitCreate}
                disabled={creating}
                className="btn-gold px-5 py-2.5 text-[0.7rem] uppercase tracking-[0.22em]"
                data-testid="submit-new-key"
              >
                {creating ? "Création…" : "Émettre la clé"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
